/**
 * The decisions the version board makes, kept out of the components that draw
 * them.
 *
 * Four of them are the reason this file exists.
 *
 * **A version that is not known is not a blank.** A component either answered
 * or it did not, and when it did not the server sends the reason instead of the
 * number. Every reading leaves here as a two-state value a component cannot
 * render as a version by accident, and nothing here substitutes the tenancy's
 * declared image tag for a reading — the tag is what somebody typed, and typing
 * it changed nothing.
 *
 * **Not read and unreadable are different things.** `read_by` says how a
 * component's version is come by at all. When it is there and the version is
 * not, the reading failed this time and can be taken again. When it is not
 * there, there is no way to take it: nothing a connector sends carries a version,
 * and a client enrols nothing at all. The second case is permanent and the
 * screen has to say which one it is looking at.
 *
 * **Four verdicts, one of them red.** `no_named_break` is the ordinary state of
 * a fleet with two versions in it. Colouring it as trouble would teach whoever
 * reads the board to stop reading it; presenting it as a guarantee would be a
 * claim gdsgate does not make. Only `broken` is red.
 *
 * **A refusal to sign off is the server's sentence, not ours.** `blocked_by`
 * already names the tenancies somebody has to go and look at. Nothing here
 * composes a replacement for it or reduces it to a code. The same holds for the
 * refusal to move a wave's stragglers again.
 *
 * **A rollout that ended is not a rollout that never happened.** It stays on
 * the board, because it is what answers "why is half the fleet on the new
 * version" — so whether one is *going* is read off `live` and never off the
 * presence of the block or off a list of state names kept here. Only a going
 * rollout can be signed off, tried again or stopped; only when none is going
 * can another be started.
 *
 * The contract is `gdsgate-saas`, docs/api.md §6c.
 */

import type {
  ComponentPair,
  ComponentVersion,
  Rollout,
  RolloutBoard,
  RolloutWave,
  TenantMove,
  TenantVersions,
  WaveSummary,
} from '../../api/types';
import { isRolloutWave } from '../../api/types';
import type { SimpleKey } from '../../i18n/types';
import type { Tone } from '../../ui/StatusBadge';

// ---------------------------------------------------------------------------
// What one component is running, or why that is not known
// ---------------------------------------------------------------------------

/**
 * A version that was read, or one that was not — and there is no third state.
 *
 * `readBy` travels with both: on a reading it says how much the answer is worth,
 * and on an absence its presence or absence is the whole difference between
 * "not read this time" and "there is no way to read this".
 */
export type Reading =
  | { read: true; version: string; readBy: string | undefined }
  | { read: false; reason: string | undefined; readBy: string | undefined };

export function readingOf(component: ComponentVersion): Reading {
  const version = component.version;
  if (version !== undefined && version !== '') {
    return { read: true, version, readBy: component.read_by };
  }
  return { read: false, reason: component.reason, readBy: component.read_by };
}

/**
 * Whether this component can be read at all.
 *
 * The server publishes the method, so this is read off the answer rather than
 * hard-coded here: a console holding its own list of which components can be
 * asked would keep saying so on the day one of them can.
 */
export function readable(component: ComponentVersion): boolean {
  return component.read_by !== undefined && component.read_by !== '';
}

/** A component that answered nothing and has no way of being asked. */
export function unreadable(component: ComponentVersion): boolean {
  return !readable(component) && readingOf(component).read === false;
}

/**
 * The components that cannot be read anywhere in this fleet, with the server's
 * reason for each.
 *
 * Named on the board rather than left to be noticed tenancy by tenancy: these
 * are the components that drift furthest — a connector runs inside the customer's
 * own network and is upgraded by the customer — so the pair most likely to be
 * the one out of step is the pair nothing here can measure. That is worth
 * saying out loud in one place.
 */
export function unreadableComponents(board: RolloutBoard): ComponentVersion[] {
  const found = new Map<string, ComponentVersion>();
  for (const row of board.tenants) {
    for (const component of row.components) {
      if (unreadable(component) && !found.has(component.component)) {
        found.set(component.component, component);
      }
    }
  }
  return [...found.values()];
}

/**
 * The pairs of one tenancy's components that nothing can measure, because at
 * least one end cannot be read at all.
 *
 * Derived from that tenancy's own answer, not assumed: a pair is listed here
 * only when the board says one of its ends has no way of being asked.
 */
export function unmeasurablePairs(row: TenantVersions): ComponentPair[] {
  const beyondReach = new Set(
    row.components.filter(unreadable).map((component) => component.component),
  );
  return row.pairs.filter((pair) => pair.between.some((end) => beyondReach.has(end)));
}

/** The same, across every tenancy the caller can see, as a set of pair names. */
export function unmeasurablePairsAcross(board: RolloutBoard): [string, string][] {
  const found = new Map<string, [string, string]>();
  for (const row of board.tenants) {
    for (const pair of unmeasurablePairs(row)) {
      found.set(pair.between.join('\0'), pair.between);
    }
  }
  return [...found.values()];
}

// ---------------------------------------------------------------------------
// The four verdicts
// ---------------------------------------------------------------------------

/**
 * How a verdict is coloured, and only one of them is red.
 *
 * `no_named_break` is deliberately not a warning. gdsgate promises compatibility
 * inside one product tag and expects a fleet to move as a whole, so two
 * different versions are the ordinary state of a fleet mid-rollout — a board
 * that flagged every mismatch would be a board nobody reads on the day one
 * matters. It is deliberately not a success either: nothing here says those two
 * versions work together, only that nothing known stands between them.
 */
const VERDICT_TONE: Record<string, Tone> = {
  same: 'ok',
  no_named_break: 'info',
  broken: 'deny',
  untold: 'neutral',
};

const VERDICT_LABEL: Record<string, SimpleKey> = {
  same: 'rollout.verdict.same',
  no_named_break: 'rollout.verdict.noNamedBreak',
  broken: 'rollout.verdict.broken',
  untold: 'rollout.verdict.untold',
};

const VERDICT_MEANING: Record<string, SimpleKey> = {
  same: 'rollout.verdict.same.meaning',
  no_named_break: 'rollout.verdict.noNamedBreak.meaning',
  broken: 'rollout.verdict.broken.meaning',
  untold: 'rollout.verdict.untold.meaning',
};

export function verdictTone(verdict: string): Tone {
  return VERDICT_TONE[verdict] ?? 'neutral';
}

export function verdictLabelKey(verdict: string): SimpleKey {
  return VERDICT_LABEL[verdict] ?? 'rollout.verdict.unknown';
}

export function verdictMeaningKey(verdict: string): SimpleKey {
  return VERDICT_MEANING[verdict] ?? 'rollout.verdict.unknown.meaning';
}

/** The one verdict worth stopping for. */
export function isBroken(pair: ComponentPair): boolean {
  return pair.verdict === 'broken';
}

export function brokenPairs(row: TenantVersions): ComponentPair[] {
  return row.pairs.filter(isBroken);
}

/**
 * The four verdicts in the order a row is judged by, worst first.
 *
 * A row on a board carries one badge for four pairs, so something has to decide
 * which of the four it is. Worst wins, and "worst" is not the same as "reddest":
 * `untold` beats `no_named_break` because a pair nothing could read is a pair
 * nobody has cleared, while `no_named_break` is a pair that was compared and
 * came back with nothing named against it. A row that showed the reassuring one
 * of the two would be the board hiding its own blind spot.
 */
const VERDICT_ORDER: string[] = ['broken', 'untold', 'no_named_break', 'same'];

/** Where a verdict stands, worst first. A verdict neither side knows sorts as worst. */
export function verdictRank(verdict: string): number {
  const at = VERDICT_ORDER.indexOf(verdict);
  return at === -1 ? -1 : at;
}

/**
 * The one verdict that stands for a whole row.
 *
 * `undefined` when the answer carried no pairs at all, which is not the same as
 * every pair agreeing and must not draw as it.
 */
export function worstVerdict(row: TenantVersions): string | undefined {
  let worst: string | undefined;
  for (const pair of row.pairs) {
    if (worst === undefined || verdictRank(pair.verdict) < verdictRank(worst)) {
      worst = pair.verdict;
    }
  }
  return worst;
}

/** One component of a row, by the name the server writes it under. */
export function componentOf(row: TenantVersions, component: string): ComponentVersion | undefined {
  return row.components.find((candidate) => candidate.component === component);
}

// ---------------------------------------------------------------------------
// The rollout itself
// ---------------------------------------------------------------------------

const ROLLOUT_STATE_TONE: Record<string, Tone> = {
  applying: 'info',
  waiting: 'warn',
  halted: 'neutral',
  done: 'ok',
};

const ROLLOUT_STATE_LABEL: Record<string, SimpleKey> = {
  applying: 'rollout.state.applying',
  waiting: 'rollout.state.waiting',
  halted: 'rollout.state.halted',
  done: 'rollout.state.done',
};

export function rolloutStateTone(state: string): Tone {
  return ROLLOUT_STATE_TONE[state] ?? 'neutral';
}

export function rolloutStateLabelKey(state: string): SimpleKey {
  return ROLLOUT_STATE_LABEL[state] ?? 'rollout.state.unknown';
}

const MOVE_TONE: Record<string, Tone> = {
  pending: 'neutral',
  moved: 'ok',
  failed: 'deny',
};

const MOVE_LABEL: Record<string, SimpleKey> = {
  pending: 'rollout.move.pending',
  moved: 'rollout.move.moved',
  failed: 'rollout.move.failed',
};

export function moveTone(outcome: string): Tone {
  return MOVE_TONE[outcome] ?? 'neutral';
}

export function moveLabelKey(outcome: string): SimpleKey {
  return MOVE_LABEL[outcome] ?? 'rollout.move.unknown';
}

/**
 * A tenancy that failed and was moved after somebody put it right.
 *
 * `moved` with a failure behind it is not the same fact as `moved`, and the
 * board is where the difference has to show: a wave signed off after a repair
 * would otherwise read like a wave that never needed one, and the only trace
 * left would be a row in the trail nobody is looking at.
 */
export function movedAfterFailure(step: TenantMove): boolean {
  return step.outcome === 'moved' && step.failures > 0;
}

/**
 * How a step is drawn — the outcome, and whether it took more than one go.
 *
 * A repaired move is deliberately neither the green of a clean one nor the red
 * of a failure: it did work in the end, and it did not work first time.
 */
export function stepTone(step: TenantMove): Tone {
  return movedAfterFailure(step) ? 'warn' : moveTone(step.outcome);
}

export function stepLabelKey(step: TenantMove): SimpleKey {
  return movedAfterFailure(step) ? 'rollout.move.movedAfterFailure' : moveLabelKey(step.outcome);
}

/**
 * Whether the board should refresh itself.
 *
 * Only while a wave is actually moving, and it costs nobody anything: the board
 * is assembled from the control plane's own storage — the readings were taken
 * when each tenancy was last reconciled — so re-reading it asks no tenancy's
 * cluster anything and writes nothing to anybody's audit trail. Once the wave
 * stops moving there is nothing to watch: what happens next is a person's
 * decision.
 */
export function movingNow(board: RolloutBoard): boolean {
  return board.rollout?.state === 'applying';
}

/** The waves in the order they run, whatever order the answer listed them in. */
export function waveOrder(board: RolloutBoard): WaveSummary[] {
  return [...board.waves].sort((a, b) => a.position - b.position);
}

export function waveLabelKey(wave: string): SimpleKey {
  if (wave === 'canary') return 'rollout.wave.canary';
  if (wave === 'rest') return 'rollout.wave.rest';
  return 'rollout.wave.unknown';
}

/** A wave a `PUT` may name. A spelling neither side knows is not offered. */
export function knownWave(wave: string): RolloutWave | null {
  return isRolloutWave(wave) ? wave : null;
}

/**
 * What signing off would do, or why it is not on offer.
 *
 * The ordering rule of the whole screen lives here: **there is no next wave
 * until the one under way has been signed off.** So `next` is never something
 * this console works out from the list of waves — it is offered only when the
 * server says the sign-off may happen, and the server is the one that knows
 * whether the wave stood long enough and whether anything in it is wrong.
 */
export type AdvanceOffer =
  | { offered: true; finishes: false; next: string }
  | { offered: true; finishes: true }
  | { offered: false; blockedBy: string | undefined };

export function advanceOffer(rollout: Rollout): AdvanceOffer {
  const advance = rollout.advance;
  if (!advance.may) return { offered: false, blockedBy: advance.blocked_by };
  const next = advance.next;
  if (next === undefined || next === '') return { offered: true, finishes: true };
  return { offered: true, finishes: false, next };
}

/**
 * What moving the wave's stragglers again would do, or why it is not on offer.
 *
 * The tenancies are the server's list — the ones of this wave that did not
 * move — and they are shown rather than counted, because "these three will be
 * moved again" is the sentence somebody needs before pressing it. The refusal
 * is the server's too: one of its four is "everything in this wave moved", and
 * that one says the thing still holding the wave is about a tenancy rather than
 * about a move, which sends somebody to a card instead of to this button.
 */
export type RetryOffer =
  | { offered: true; tenants: string[] }
  | { offered: false; blockedBy: string | undefined };

export function retryOffer(rollout: Rollout): RetryOffer {
  const retry = rollout.retry;
  if (!retry.may) return { offered: false, blockedBy: retry.blocked_by };
  return { offered: true, tenants: retry.tenants ?? [] };
}

/**
 * Whether this rollout is still going, which is the only thing that decides
 * what a person may do to it.
 *
 * Read off `live` and never off `state`: a check written as "not halted" said
 * nothing about `done`, and both of those are ended rollouts that go on being
 * shown. Signing a wave off, moving stragglers again and stopping all belong to
 * a rollout that is going; an ended one has no wave to sign, nothing left to
 * move and nothing to stop, and the server answers all three with `409`.
 */
export function stillGoing(rollout: Rollout): boolean {
  return rollout.live;
}

/**
 * Whether a rollout may be begun: there is no second **going** one.
 *
 * An ended rollout does not stand in the way. Putting a fleet back is another
 * rollout aimed at the version it came from, and refusing that on the strength
 * of the rollout it is undoing would leave an operator with the fleet where the
 * trouble is and no way out.
 */
export function mayStart(board: RolloutBoard): boolean {
  const rollout = board.rollout;
  return rollout === undefined || !stillGoing(rollout);
}

/**
 * The first wave, which is the one a rollout starts with.
 *
 * A rollout whose first wave is empty is refused by the server, and the start
 * dialog says so beforehand rather than letting somebody find out by being
 * refused: it would be a rollout, not a canary rollout — the whole fleet moving
 * on the strength of a wave that showed nothing.
 */
export function firstWave(board: RolloutBoard): WaveSummary | undefined {
  return waveOrder(board)[0];
}

/** The tenancies of one wave that a rollout may move, in slug order — the order they move in. */
export function waveMembers(board: RolloutBoard, wave: string): TenantVersions[] {
  return board.tenants
    .filter((row) => row.wave === wave && row.movable)
    .sort((a, b) => a.slug.localeCompare(b.slug));
}

/**
 * The tenancies no wave moves, with the state that keeps them out.
 *
 * Shown rather than hidden: "why is this one not moving" is asked on this
 * screen, so it is answered on this screen.
 */
export function outsideEveryWave(board: RolloutBoard): TenantVersions[] {
  return board.tenants.filter((row) => !row.movable);
}

/**
 * What has already happened to the wave under way, tenancy by tenancy.
 *
 * The four lists are what somebody signing a wave off is looking at: what moved
 * cleanly, what moved only after being put right, what did not move, and what
 * has not had its turn. A wave with anything in the last two cannot be signed
 * off, and the server's refusal names exactly those tenancies.
 *
 * `repaired` is a list of its own rather than part of `moved` because it is a
 * different fact, and this is the screen where it matters most: a wave signed
 * off with a repair in it should not look like a wave that never needed one.
 */
export interface WaveProgress {
  moved: TenantVersions[];
  repaired: TenantVersions[];
  failed: TenantVersions[];
  pending: TenantVersions[];
}

/**
 * The four figures above the board while a rollout is on record: what moved,
 * what did not, what has not had its turn, and what the move crosses.
 *
 * Arithmetic over the answer that already arrived, and nothing else — no second
 * request and no figure the server did not send. A tenancy with no step of its
 * own is in none of the three: it is not "not moved yet", it is one no rollout
 * has reached, which is what the wave counters below the tiles are for.
 */
export interface StepTally {
  moved: number;
  failed: number;
  pending: number;
}

export function stepTally(board: RolloutBoard): StepTally {
  const outcomes = board.tenants.map((row) => row.step?.outcome);
  return {
    moved: outcomes.filter((outcome) => outcome === 'moved').length,
    failed: outcomes.filter((outcome) => outcome === 'failed').length,
    pending: outcomes.filter((outcome) => outcome === 'pending').length,
  };
}

/**
 * The four figures above the board when no rollout is on record: how big the
 * fleet is, how many versions it is declared with, how many tenancies carry a
 * named break of their own, and how many no rollout would move.
 */
export interface FleetTally {
  tenants: number;
  declared: number;
  broken: number;
  notMovable: number;
}

export function fleetTally(board: RolloutBoard): FleetTally {
  return {
    tenants: board.tenants.length,
    declared: new Set(board.tenants.map((row) => row.declared)).size,
    broken: board.tenants.filter((row) => brokenPairs(row).length > 0).length,
    notMovable: board.tenants.filter((row) => !row.movable).length,
  };
}

export function waveProgress(board: RolloutBoard, wave: string): WaveProgress {
  const members = waveMembers(board, wave);
  return {
    moved: members.filter(
      (row) => row.step?.outcome === 'moved' && !movedAfterFailure(row.step),
    ),
    repaired: members.filter((row) => row.step !== undefined && movedAfterFailure(row.step)),
    failed: members.filter((row) => row.step?.outcome === 'failed'),
    // No step at all is the same thing as a step that has not come out yet:
    // its turn has not arrived, and either way nobody has moved it.
    pending: members.filter(
      (row) => row.step === undefined || row.step.outcome === 'pending',
    ),
  };
}
